import React, { useEffect, useContext } from "react";
import { AppContext } from "../../contexts/AppContext";
import PropTypes from "prop-types";
import { Form, Input, Checkbox, Radio } from "antd";
import { QUESTION_TYPES } from "../../config/setting";
import "./QuestionnaireInputForm.css";

const QuestionnaireInputForm = ({ questions, form, errors, setErrors }) => {
  const { globalLoading } = useContext(AppContext);

  // 初回マウント時にセッションストレージからデータを復元
  useEffect(() => {
    const loadDatas = async () => {
      const savedData = sessionStorage.getItem("inputForm");
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        form.setFieldsValue(parsedData);
      }
    };

    loadDatas();
  }, [globalLoading]);

  // ページがリロードまたは閉じる直前にセッションストレージにstateを保存
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && form) {
        sessionStorage.setItem(
          "inputForm",
          JSON.stringify(form.getFieldsValue())
        );
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [form]);

  /**
   * フィールドのフォーカスが外れた時の処理
   * @param {string} fieldName - フィールド名
   */
  const onValueChange = async (fieldName) => {
    try {
      await form.validateFields([fieldName]);
      setErrors({ ...errors, [fieldName]: false });
    } catch (error) {
      setErrors({ ...errors, [fieldName]: true });
    }
  };

  return (
    <div className="questionnaire-area">
      {questions.map((question) => (
        <Form.Item
          className="ant-label"
          key={question.questionId}
          label={
            <div className="question-label">
              {question.isRequired && <div className="emphasis-icon">必須</div>}
              <div className="question-label-content">
                {question.questionContent}
              </div>
            </div>
          }
          name={`question-${question.questionId}`}
          rules={[{ required: question.isRequired, message: "必須項目です" }]}
          validateStatus={
            errors[`question-${question.questionId}`] ? "error" : ""
          }
          help={
            errors[`question-${question.questionId}`] &&
            (question.questionType === QUESTION_TYPES.SINGLE ||
              question.questionType === QUESTION_TYPES.MULTIPLE) ? (
              <span className="ant-validation-message">選択してください</span>
            ) : errors[`question-${question.questionId}`] &&
              question.questionType != QUESTION_TYPES.SINGLE &&
              question.questionType != QUESTION_TYPES.MULTIPLE ? (
              <span className="ant-validation-message">入力をお願いします</span>
            ) : (
              ""
            )
          }
        >
          {question.questionType === QUESTION_TYPES.MULTIPLE && (
            <Checkbox.Group
              onChange={() => onValueChange(`question-${question.questionId}`)}
            >
              {question.options.map((option) => (
                <Checkbox
                  key={option.optionPosition}
                  value={option.optionContent}
                >
                  <span className="answer-label">{option.optionContent}</span>
                </Checkbox>
              ))}
            </Checkbox.Group>
          )}
          {question.questionType === QUESTION_TYPES.SINGLE && (
            <Radio.Group
              onChange={() => onValueChange(`question-${question.questionId}`)}
            >
              {question.options.map((option) => (
                <Radio key={option.optionPosition} value={option.optionContent}>
                  <span className="answer-label">{option.optionContent}</span>
                </Radio>
              ))}
            </Radio.Group>
          )}
          {question.questionType === QUESTION_TYPES.TEXT_FIELD && (
            <Input
              className="answer-label"
              onChange={() => onValueChange(`question-${question.questionId}`)}
            />
          )}
          {question.questionType === QUESTION_TYPES.TEXT_AREA && (
            <Input.TextArea
              className="answer-label"
              rows={5}
              onChange={() => onValueChange(`question-${question.questionId}`)}
            />
          )}
        </Form.Item>
      ))}
    </div>
  );
};

QuestionnaireInputForm.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      questionId: PropTypes.number.isRequired,
      questionContent: PropTypes.string.isRequired,
      questionType: PropTypes.oneOf([
        QUESTION_TYPES.MULTIPLE,
        QUESTION_TYPES.SINGLE,
        QUESTION_TYPES.TEXT,
      ]).isRequired,
      required: PropTypes.bool.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          optionPosition: PropTypes.number.isRequired,
          optionContent: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
};

export default QuestionnaireInputForm;
